import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import DischargeConfirm from 'components/dischargeForm/DischargeConfirm';
import DischargeFileRequests from 'components/dischargeForm/DischargeFileRequests';
import DischargeInformation, {
  DischargeFormMeta,
  DischargeFormValues,
} from 'components/dischargeForm/DischargeInformation';
import DischargeQuestions, { DischargeQuestion } from 'components/dischargeForm/DischargeQuestions';
import {
  buildDischargeFormSteps,
  buildLinkedDischargeFormSteps,
  DischargeSteps,
} from 'components/dischargeForm/helpers';
import LinkedDischargeQuestions from 'components/dischargeForm/LinkedDischargeQuestions';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import { PatientName } from 'components/shared/StandardModal';
import { Flags } from 'constants/flags';
import { useForm } from 'context/form';
import { useProfile } from 'context/profile';
import { DocumentTypes } from 'models/Attachment';
import { ProviderGroupTypeConfig } from 'models/GroupType';
import LocationEpisode from 'models/LocationEpisode';
import { QuestionKind } from 'models/QuestionTemplate';

type Props = {
  patientName: string;
  locationEpisode: LocationEpisode;
  linkedLocationEpisodes?: LocationEpisode[];
  onCancel: () => void;
  dischargeQuestionsFlagEnabled: boolean;
  showActualDischargeDate?: boolean;
  onSubtitleChange?: (subtitle: string | undefined) => void;
};

const REQUESTED_DOCS = [DocumentTypes.DischargeSummary];

export default function DischargeForm({
  locationEpisode,
  linkedLocationEpisodes = [],
  patientName,
  onCancel,
  dischargeQuestionsFlagEnabled,
  showActualDischargeDate = false,
  onSubtitleChange,
}: Props) {
  const hasLinkedLEs = linkedLocationEpisodes.length > 0;
  const { profile } = useProfile();
  // On edit discharge, the location episode is replaced with one from the API response
  // which may not have group populated. Guard against missing group.id to avoid
  // isProviderFor throwing. When group.id is missing, we fall back to treating the
  // user as manager-only, which is safe because edit discharge doesn't show linked DQs.
  const isManagerOnly = useMemo(
    () =>
      !!profile?.canAccessGroup(locationEpisode.owner.id) &&
      (!locationEpisode.group?.id || !profile?.isProviderFor(locationEpisode.group.id)),
    [profile, locationEpisode.owner.id, locationEpisode.group?.id]
  );
  const isAffiliateUser = useMemo(() => profile?.isAffiliateUser, [profile]);
  const [step, setStep] = useState<string>(DischargeSteps.Information);

  const groupTypeConfig = (locationEpisode.group.groupType.config ?? {}) as ProviderGroupTypeConfig;

  const { valid, dirty, values, submitForm, meta } = useForm<DischargeFormValues, DischargeFormMeta>();

  const questions = useMemo(
    () => (dischargeQuestionsFlagEnabled ? (locationEpisode.dischargeTemplate?.questions ?? []) : []),
    [dischargeQuestionsFlagEnabled, locationEpisode.dischargeTemplate?.questions]
  );

  const hasDischargeQuestions = !!questions.length && !isManagerOnly && !isAffiliateUser;
  const hasAnyDischargeQuestions = useMemo(() => {
    if (isManagerOnly || isAffiliateUser) return false;
    if (hasDischargeQuestions) return true;
    return linkedLocationEpisodes.some(
      (le) => le.owner?.client?.hasFlag(Flags.DischargeQuestions) && !!le.dischargeTemplate?.questions?.length
    );
  }, [hasDischargeQuestions, linkedLocationEpisodes, isManagerOnly, isAffiliateUser]);
  const canContinueCare =
    !!values.dischargeReason?.dischargedToGroupType?.isProvider && values.dischargedGroup?.name !== 'Other';
  const hasAllRequestedFiles = REQUESTED_DOCS.every((doc) =>
    (values.note?.attachments || []).some((a) => a.docType === doc)
  );

  const steps = useMemo(
    () =>
      hasLinkedLEs
        ? buildLinkedDischargeFormSteps({
            primaryLE: locationEpisode,
            linkedLEs: linkedLocationEpisodes,
            hasDischargeQuestions: hasAnyDischargeQuestions,
            canContinueCare,
            hasAllRequestedFiles,
            hideDischargeSummaryPrompt: groupTypeConfig.hideDischargeSummaryPrompt,
            currentStep: step,
          })
        : buildDischargeFormSteps({
            hasDischargeQuestions,
            canContinueCare,
            hasAllRequestedFiles,
            hideDischargeSummaryPrompt: groupTypeConfig.hideDischargeSummaryPrompt,
            currentStep: step,
          }),
    [
      step,
      hasLinkedLEs,
      locationEpisode,
      linkedLocationEpisodes,
      hasDischargeQuestions,
      hasAnyDischargeQuestions,
      canContinueCare,
      hasAllRequestedFiles,
      groupTypeConfig.hideDischargeSummaryPrompt,
    ]
  );

  const hasMoreSteps = useMemo(() => steps.indexOf(step) < steps.length - 1, [step, steps]);

  const onPreviousStep = () => {
    if (step === DischargeSteps.Information) {
      onCancel();
      return;
    }

    const currentIndex = steps.indexOf(step);
    setStep(steps[currentIndex - 1]);
  };

  const onNextStep = () => {
    if (!hasMoreSteps) {
      submitForm();
      return;
    }

    const currentIndex = steps.indexOf(step);
    setStep(steps[currentIndex + 1]);
  };

  const dischargeQuestions: DischargeQuestion[] = useMemo(() => {
    const satisfactionScaleQuestions = questions.filter((q) => q.kind === QuestionKind.SATISFACTION_SCALE);
    const satisfactionScaleValues = satisfactionScaleQuestions.map((q) => values.questions?.[q.id]);

    return questions.map((q) => {
      if (
        q.config.optional ||
        // if satisfaction scale questions are present and none of their values match any of the note's
        // configured required ratings, make the note question optional on the form
        (satisfactionScaleQuestions.length &&
          q.kind === QuestionKind.NOTES &&
          q.config.requiredRatings?.length &&
          q.config.requiredRatings.every((rating) =>
            satisfactionScaleValues.every((value) => value !== `${rating} out of 5`)
          ))
      ) {
        return { ...q, optional: true };
      }

      return q;
    });
  }, [questions, values.questions]);

  const allRequiredQuestionsAnswered = useMemo(() => {
    return dischargeQuestions.every((q) => q.optional || values.questions?.[q.id]);
  }, [dischargeQuestions, values.questions]);

  const currentStepQuestionsAnswered = useMemo(() => {
    if (!step.startsWith('questions_')) return true;
    const leId = step.replace('questions_', '');
    const allLEs = [locationEpisode, ...linkedLocationEpisodes];
    const targetLE = allLEs.find((le) => le.id === leId);
    if (!targetLE) return true;

    const stepQuestions = targetLE.dischargeTemplate?.questions ?? [];
    const isPrimary = targetLE.id === locationEpisode.id;
    const answers = isPrimary ? values.questions : values.linkedQuestions?.[leId];

    return stepQuestions.every((q) => q.config?.optional || answers?.[q.id]);
  }, [step, locationEpisode, linkedLocationEpisodes, values.questions, values.linkedQuestions]);

  const questionSteps = useMemo(() => steps.filter((s) => s.startsWith('questions_')), [steps]);

  useEffect(() => {
    if (!step.startsWith('questions_') || questionSteps.length <= 1) {
      onSubtitleChange?.(undefined);
      return;
    }
    const leId = step.replace('questions_', '');
    const allLEs = [locationEpisode, ...linkedLocationEpisodes];
    const targetLE = allLEs.find((le) => le.id === leId);
    if (!targetLE) {
      onSubtitleChange?.(undefined);
      return;
    }
    const stepIndex = questionSteps.indexOf(step);
    const clientName = targetLE.owner?.client?.name ?? targetLE.group?.name;
    onSubtitleChange?.(`Record ${stepIndex + 1} of ${questionSteps.length}: ${clientName}`);
  }, [step, questionSteps, locationEpisode, linkedLocationEpisodes, onSubtitleChange]);

  return (
    <>
      <PatientName>Patient: {patientName}</PatientName>

      {step === DischargeSteps.Information && (
        <DischargeInformation
          locationEpisode={locationEpisode}
          showActualDischargeDate={showActualDischargeDate}
          hideDischargedAma={groupTypeConfig.hideDischargedAma}
          taggable={!hasLinkedLEs}
        />
      )}
      {step === DischargeSteps.FileRequests && <DischargeFileRequests />}
      {!hasLinkedLEs && step === DischargeSteps.Questions && <DischargeQuestions questions={dischargeQuestions} />}
      {hasLinkedLEs && step.startsWith('questions_') && (
        <LinkedDischargeQuestions step={step} primaryLE={locationEpisode} linkedLEs={linkedLocationEpisodes} />
      )}
      {step === DischargeSteps.Confirm && <DischargeConfirm />}

      <Actions>
        <Button variant='ghost' type='button' onClick={onPreviousStep}>
          {step === DischargeSteps.Information ? 'Cancel' : 'Back'}
        </Button>
        <Button
          type='button'
          disabled={
            !valid ||
            !dirty ||
            meta.isUploadingAttachments ||
            (step === DischargeSteps.Questions && !allRequiredQuestionsAnswered) ||
            (step.startsWith('questions_') && !currentStepQuestionsAnswered)
          }
          loading={meta.isSubmitting}
          onClick={onNextStep}>
          {hasMoreSteps ? 'Next' : 'Confirm'}
        </Button>
      </Actions>
    </>
  );
}

const Actions = styled(ButtonGroup)`
  margin-top: 24px;
  justify-content: flex-end;
`;
